import { useCallback, useEffect, useRef, useState } from 'react';
import '../../../shared/styles/Orchestrator.css';
import '../../../styles/Dice.css';
import GridBoard from './GridBoard';
import BreakScreen from '../../../shared/components/BreakScreen';
import { getStoredSceneId, loadScenes } from '../utils/sceneStorage';
import {
  DEFAULT_GRID_COLS,
  DEFAULT_GRID_ROWS,
  DEFAULT_GRID_TYPE,
  hexGridAspectRatio,
} from '../utils/gridConfig';
import {
  DEFAULT_GRID_VIEW_STATE,
  normalizeGridViewState,
} from '../utils/gridViewState';
import { getGridViewportMetrics } from '../utils/gridViewport';
import DiceBox from '@3d-dice/dice-box';
import { buildDisplayFromResults } from '../utils/diceCommands';
import { normalizeSessionToken } from '../utils/sessions';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  showDisplayRollToast,
  syncDisplayTrackerToasts,
  applyDisplayTrackerToastAction,
  WorldMapOverlay,
} from './OrchestratorDisplayHelpers';

let globalDiceBox = null;

function OrchestratorDisplay() {
  const [scene, setScene] = useState(null);
  const [rangeSourceId, setRangeSourceId] = useState(null);
  const [pings, setPings] = useState([]);
  const [sessionToken, setSessionToken] = useState(() => {
    if (typeof window === 'undefined') return null;
    return normalizeSessionToken(localStorage.getItem('orchestratorSessionToken')) || null;
  });
  const [diceBoxReady, setDiceBoxReady] = useState(false);
  const [isReversed, setIsReversed] = useState(() => {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem('displayReversed') === 'true';
  });
  const [isOnBreak, setIsOnBreak] = useState(() => {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem('orchestratorBreak') === 'true';
  });
  const [showNames, setShowNames] = useState(false);
  const [gridConfig, setGridConfig] = useState({
    cols: DEFAULT_GRID_COLS,
    rows: DEFAULT_GRID_ROWS,
    type: DEFAULT_GRID_TYPE,
  });
  const [displayViewState, setDisplayViewState] = useState(DEFAULT_GRID_VIEW_STATE);
  const [displayBoardSize, setDisplayBoardSize] = useState({ width: 0, height: 0 });
  const gridConfigRef = useRef(gridConfig);
  const [mapOverlayData, setMapOverlayData] = useState({
    active: false,
    worldMap: null,
    view: { panX: 0, panY: 0, zoom: 1 },
  });
  const channelRef = useRef(null);
  const channelClosedRef = useRef(false);
  const boardRef = useRef(null);
  const scrollerRef = useRef(null);
  const displayViewStateRef = useRef(displayViewState);
  const sceneIdRef = useRef(null);
  const diceBoxRef = useRef(null);
  const lastRollIdRef = useRef(null);
  const pendingRollEventRef = useRef(null);
  // Stores { rollId, roll, parsed } so onRollComplete can read roll params
  // after the animation finishes (pendingRollEventRef is cleared before roll starts).
  const pendingRollParamsRef = useRef(null);

  useEffect(() => {
    setGridConfig(scene?.gridConfig || {
      cols: DEFAULT_GRID_COLS,
      rows: DEFAULT_GRID_ROWS,
      type: DEFAULT_GRID_TYPE,
    });
  }, [scene]);

  useEffect(() => {
    displayViewStateRef.current = displayViewState;
  }, [displayViewState]);

  useEffect(() => {
    gridConfigRef.current = gridConfig;
  }, [gridConfig]);

  useEffect(() => {
    sceneIdRef.current = scene?.id || null;
  }, [scene?.id]);
  const pingTimeoutsRef = useRef(new Set());
  const safePostMessage = useCallback((payload) => {
    const channel = channelRef.current;
    if (!channel || channelClosedRef.current) return;
    try {
      channel.postMessage(payload);
    } catch (error) {
      console.warn('BroadcastChannel send failed', error);
    }
  }, []);

  useEffect(() => {
    const username = localStorage.getItem('username');
    const system = localStorage.getItem('system');
    if (!username || system !== 'orchestrator') {
      window.close();
    }
  }, []);

  // Cleanup global dice box and pending ping timers on unmount to avoid hanging WebGL contexts and timers.
  useEffect(() => {
    return () => {
      pingTimeoutsRef.current.forEach((id) => clearTimeout(id));
      pingTimeoutsRef.current.clear();

      if (diceBoxRef.current) {
        try {
          if (typeof diceBoxRef.current.clear === 'function') {
            diceBoxRef.current.clear();
          }
          if (typeof diceBoxRef.current.destroy === 'function') {
            diceBoxRef.current.destroy();
          }
        } catch (e) {
          // Best-effort cleanup only
          console.warn('DiceBox cleanup warning', e);
        }
      }
      diceBoxRef.current = null;
      globalDiceBox = null;
      channelClosedRef.current = true;
      if (channelRef.current) {
        try {
          channelRef.current.close();
        } catch (e) {
          console.warn('BroadcastChannel close warning', e);
        }
      }
      channelRef.current = null;
    };
  }, []);

  // Attach (or re-attach) the onRollComplete callback on a ready dice box.
  // Using a separate function so it can be called in both init paths.
  const attachRollComplete = useCallback((box) => {
    box.onRollComplete = (results) => {
      const params = pendingRollParamsRef.current;
      pendingRollParamsRef.current = null;
      if (!params) return;

      const { rollId, roll } = params;
      const parsed = roll?.parsed ?? null;

      // Compute displayText and data from the actual 3D dice values.
      let displayText = roll?.displayText ?? '';
      let rollData = roll?.data ?? {};
      if (parsed && Array.isArray(results) && results.length > 0) {
        const built = buildDisplayFromResults(parsed, results);
        if (built) {
          displayText = built.displayText;
          rollData = built.data;
        }
      }

      // Signal the Manager with actual values so it can write Firebase and
      // toast the GM/player views after the animation has finished.
      safePostMessage({ type: 'rollComplete', rollId, displayText, data: rollData });

      // Show the result on the Display screen too.
      try {
        showDisplayRollToast(roll, displayText);
      } catch (e) {
        console.warn('Display roll-complete toast failed', e);
      }
    };
  }, [safePostMessage]);

  // Initialize 3D dice tray on the display.
  useEffect(() => {
    if (!globalDiceBox) {
      globalDiceBox = new DiceBox('#dice-box', {
        id: 'dice-canvas',
        assetPath: '/assets/dice-box/',
        startingHeight: 10,
        throwForce: 6,
        spinForce: 5,
        lightIntensity: 0.9,
        scale: 4,
        themeColor: '#e94f37',
      });

      globalDiceBox
        .init()
        .then(() => {
          diceBoxRef.current = globalDiceBox;
          attachRollComplete(diceBoxRef.current);
          setDiceBoxReady(true);
        })
        .catch((error) => {
          console.error('Failed to initialize dice box:', error);
        });
    } else {
      diceBoxRef.current = globalDiceBox;
      attachRollComplete(diceBoxRef.current);
      setDiceBoxReady(true);
    }
  }, [attachRollComplete]);

  const flushPendingRoll = useCallback(() => {
    if (!diceBoxRef.current) return;
    const data = pendingRollEventRef.current;
    if (!data) return;

    const rollId = String(data.rollId || '').trim();
    if (rollId && lastRollIdRef.current === rollId) {
      pendingRollEventRef.current = null;
      return;
    }
    if (rollId) lastRollIdRef.current = rollId;

    const kind = String(data.roll?.kind ?? '').toLowerCase();
    if (kind === 'clear') {
      try {
        if (typeof diceBoxRef.current.clear === 'function') {
          diceBoxRef.current.clear();
        }
      } catch {
        // ignore
      }
      pendingRollEventRef.current = null;
      return;
    }

    // animation may be a plain string notation (e.g. "2d6") or an array of
    // per-die objects (used by Duality so Hope/Fear dice render in different colours).
    const animationRaw = data.roll?.animation;
    const notation = Array.isArray(animationRaw)
      ? animationRaw
      : String(animationRaw || '').trim();
    if (!notation || (Array.isArray(notation) && notation.length === 0)) {
      pendingRollEventRef.current = null;
      return;
    }

    // Store roll params so onRollComplete can compute the result from actual dice values.
    pendingRollParamsRef.current = { rollId, roll: data.roll };

    try {
      diceBoxRef.current.roll(notation);
    } catch (error) {
      console.warn('DiceBox roll failed', error);
      // Clear pending params if the animation can't start.
      pendingRollParamsRef.current = null;
    }
    pendingRollEventRef.current = null;
  }, []);

  useEffect(() => {
    if (!diceBoxReady) return;
    flushPendingRoll();
  }, [diceBoxReady, flushPendingRoll]);

  const reportViewport = useCallback(() => {
    if (!boardRef.current) return;
    const board = boardRef.current;
    const viewportWidth = board.offsetWidth;
    const viewportHeight = board.offsetHeight;
    if (!viewportWidth || !viewportHeight) return;
    const config = gridConfigRef.current;
    const aspectRatio = config.type === 'hex'
      ? hexGridAspectRatio(config.cols, config.rows)
      : config.cols / config.rows;
    const view = normalizeGridViewState(displayViewStateRef.current, {
      width: viewportWidth,
      height: viewportHeight,
      aspectRatio,
      fitMode: 'cover',
    });
    safePostMessage({
      type: 'displayViewport',
      rect: getGridViewportMetrics({
        width: viewportWidth,
        height: viewportHeight,
        aspectRatio,
        zoom: view.zoom,
        centerX: view.centerX,
        centerY: view.centerY,
        fitMode: 'cover',
      }),
    });
  }, [safePostMessage]);

  useEffect(() => {
    const viewport = scrollerRef.current;
    if (!viewport) return undefined;
    let resizeFrame = null;

    const syncBoardSize = () => {
      const availableWidth = viewport.clientWidth;
      const availableHeight = viewport.clientHeight;
      if (!availableWidth || !availableHeight) return;
      const width = availableWidth;
      const height = availableHeight;
      setDisplayBoardSize((previous) => (
        previous.width === width && previous.height === height
          ? previous
          : { width, height }
      ));
    };

    const scheduleBoardSizeSync = () => {
      if (resizeFrame !== null) return;
      resizeFrame = window.requestAnimationFrame(() => {
        resizeFrame = null;
        syncBoardSize();
      });
    };

    syncBoardSize();
    const observer = typeof ResizeObserver === 'undefined'
      ? null
      : new ResizeObserver(scheduleBoardSizeSync);
    observer?.observe(viewport);
    window.addEventListener('resize', scheduleBoardSizeSync);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', scheduleBoardSizeSync);
      if (resizeFrame !== null) window.cancelAnimationFrame(resizeFrame);
    };
  }, [Boolean(scene)]);

  const emitRangeSource = useCallback((tokenId) => {
    safePostMessage({ type: 'rangeSource', tokenId });
  }, [safePostMessage]);

  useEffect(() => {
    const currentId = getStoredSceneId();
    const scenes = loadScenes();
    setScene(scenes.find((item) => item.id === currentId) || null);

    const channel = new BroadcastChannel('orchestrator_channel');
    channelClosedRef.current = false;
    channelRef.current = channel;
    channel.onmessage = (event) => {
      const { data } = event;
      if (!data) return;
      if (data.type === 'sceneUpdate') {
        sceneIdRef.current = data.scene?.id || null;
        setScene(data.scene);
        channel.postMessage({ type: 'requestGridView' });
      } else if (data.type === 'gridViewState') {
        if (!data.sceneId || data.sceneId === sceneIdRef.current) {
          setDisplayViewState(normalizeGridViewState(data.viewState));
        }
      } else if (data.type === 'managerScroll' && boardRef.current) {
        const width = boardRef.current.offsetWidth;
        const height = boardRef.current.offsetHeight;
        if (!width || !height) return;
        const currentView = displayViewStateRef.current;
        const config = gridConfigRef.current;
        const aspectRatio = config.type === 'hex'
          ? hexGridAspectRatio(config.cols, config.rows)
          : config.cols / config.rows;
        const viewportMetrics = getGridViewportMetrics({
          width,
          height,
          aspectRatio,
          zoom: currentView.zoom,
          fitMode: 'cover',
        });
        const nextView = normalizeGridViewState({
          ...currentView,
          centerX: Number.isFinite(data.scrollLeft)
            ? (data.scrollLeft + width / 2) / viewportMetrics.contentWidth
            : currentView.centerX,
          centerY: Number.isFinite(data.scrollTop)
            ? (data.scrollTop + height / 2) / viewportMetrics.contentHeight
            : currentView.centerY,
        }, { width, height, aspectRatio, fitMode: 'cover' });
        displayViewStateRef.current = nextView;
        setDisplayViewState(nextView);
        reportViewport();
      } else if (data.type === 'rangeSource') {
        setRangeSourceId(data.tokenId || null);
      } else if (data.type === 'ping') {
        // Handle incoming ping from manager
        if (data.ping && typeof data.ping.x === 'number' && typeof data.ping.y === 'number') {
          const newPing = { ...data.ping };
          setPings((prevPings) => [...prevPings, newPing]);
          
          // Auto-remove ping after 3 seconds
          const timeoutId = setTimeout(() => {
            setPings((prevPings) => prevPings.filter((p) => p.id !== newPing.id));
            pingTimeoutsRef.current.delete(timeoutId);
          }, 3000);
          pingTimeoutsRef.current.add(timeoutId);
        }
      } else if (data.type === 'sessionUpdate') {
        const token = normalizeSessionToken(data.token);
        setSessionToken(token || null);
        if (token) {
          localStorage.setItem('orchestratorSessionToken', token);
        }
      } else if (data.type === 'rollEvent') {
        pendingRollEventRef.current = data;
        if (!diceBoxRef.current) return;
        flushPendingRoll();
      } else if (data.type === 'displayRotation') {
        const flipped = !!data.flipped;
        setIsReversed(flipped);
        if (typeof window !== 'undefined') {
          localStorage.setItem('displayReversed', flipped ? 'true' : 'false');
        }
      } else if (data.type === 'breakUpdate') {
        const onBreak = !!data.isOnBreak;
        setIsOnBreak(onBreak);
        if (typeof window !== 'undefined') {
          localStorage.setItem('orchestratorBreak', onBreak ? 'true' : 'false');
        }
      } else if (data.type === 'showNamesMode') {
        setShowNames(!!data.active);
      } else if (data.type === 'gridConfigUpdate') {
        if (data.gridConfig) {
          setGridConfig(data.gridConfig);
        }
      } else if (data.type === 'trackersUpdate') {
        if (Array.isArray(data.trackers)) {
          syncDisplayTrackerToasts(data.trackers);
        }
      } else if (data.type === 'trackerToast') {
        applyDisplayTrackerToastAction(data);
      } else if (data.type === 'worldMapOverlay') {
        if (data.active) {
          setMapOverlayData({
            active: true,
            worldMap: data.worldMap || null,
            view: data.view || { panX: 0, panY: 0, zoom: 1 },
          });
        } else {
          setMapOverlayData((prev) => ({ ...prev, active: false }));
        }
      } else if (data.type === 'worldMapOverlayView') {
        if (data.view) {
          setMapOverlayData((prev) => ({ ...prev, view: data.view }));
        }
      } else if (data.type === 'toast') {
        const message = typeof data.message === 'string' ? data.message : String(data.message ?? '');
        if (!message) return;
        const options = data.options && typeof data.options === 'object' ? data.options : undefined;
        const variant = typeof data.variant === 'string' ? data.variant : 'default';

        try {
          const fn = toast && variant && typeof toast[variant] === 'function' ? toast[variant] : null;
          if (fn && variant !== 'default') {
            fn(message, options);
          } else {
            toast(message, options);
          }
        } catch (error) {
          console.warn('Display toast failed', error);
        }
      }
    };
    channel.postMessage({ type: 'requestScene' });
    channel.postMessage({ type: 'requestGridView' });
    channel.postMessage({ type: 'requestDisplayRotation' });
    channel.postMessage({ type: 'requestTrackers' });
    return () => {
      channelClosedRef.current = true;
      try {
        channel.close();
      } catch (error) {
        console.warn('BroadcastChannel close warning', error);
      }
      channelRef.current = null;
    };
  }, [reportViewport]);

  useEffect(() => {
    reportViewport();
  }, [displayBoardSize, displayViewState, gridConfig, reportViewport, scene]);

  useEffect(() => {
    const board = boardRef.current;
    if (!board) return undefined;
    const observer = typeof ResizeObserver === 'undefined'
      ? null
      : new ResizeObserver(reportViewport);
    observer?.observe(board);
    window.addEventListener('resize', reportViewport);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', reportViewport);
    };
  }, [reportViewport, scene?.id]);

  useEffect(() => {
    if (!rangeSourceId || !scene) return;
    const exists = scene.tokens?.some(
      (token) => token.id === rangeSourceId && token.visible !== false
    );
    if (!exists) {
      setRangeSourceId(null);
      emitRangeSource(null);
    }
  }, [emitRangeSource, rangeSourceId, scene]);

  const cinematicModeActive = scene?.effect?.type === 'cinematicMode';

  return (
    <div className='display_window'>
      <ToastContainer
        position='bottom-center'
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme='colored'
      />
      {scene ? (
        <div className='display_viewport' ref={scrollerRef} style={{ position: 'relative' }}>
          <div
            className={`display_scroller${isReversed ? ' display_scroller_reversed' : ''}`}
            style={{ width: `${displayBoardSize.width}px`, height: `${displayBoardSize.height}px` }}
          >
            <GridBoard
              scene={scene}
              readOnly
              ref={boardRef}
              rangeSourceId={rangeSourceId}
              pings={pings}
              showNames={showNames}
              gridCols={gridConfig.cols}
              gridRows={gridConfig.rows}
              gridType={gridConfig.type}
              boardDimensions={displayBoardSize}
              boardOverlayScale={displayViewState.zoom}
              viewState={displayViewState}
              fitMode='cover'
            />
          </div>
          <WorldMapOverlay mapOverlayData={mapOverlayData} />
          {cinematicModeActive && (
            <div className='cinematic_mode_overlay' aria-hidden='true'>
              <div className='cinematic_mode_bar cinematic_mode_bar_top' />
              <div className='cinematic_mode_bar cinematic_mode_bar_bottom' />
            </div>
          )}
        </div>
      ) : (
        <div style={{ padding: '20px' }}>No scene selected.</div>
      )}
      {isOnBreak && <BreakScreen />}
    </div>
  );
}

export default OrchestratorDisplay;
